import {
  EMPTY_TASK_FLOW_ERRORS,
  TaskEditorValue,
  TaskFlowErrors,
  TaskGroupEditorValue,
} from './contracts';
import { TASK_TYPE_REGISTRY } from './taskTypeRegistry';
import { validateCollectDataSchema as validateSchema } from './schema-validation';

const DTMF_STOP_EVENTS = new Set(['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '#']);
const TASK_FLOW_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const TASK_ID_PATTERN = /^[A-Za-z0-9_]+$/;

const validateCollectDataSchema = (task: TaskEditorValue, errors: Record<string, string>) => {
  if (task.schemaType !== undefined && task.schemaType !== 'json_schema') {
    errors.schemaType = 'schemaTypeInvalid';
  }
  if (task.schema === undefined || task.schema === null) return;

  const result = validateSchema(task.schema);
  if (!result.valid) errors.schema = result.error;
};

const validateDtmf = (task: TaskEditorValue, errors: Record<string, string>) => {
  if (!Number.isInteger(task.numDigits) || task.numDigits! < 1 || task.numDigits! > 32) {
    errors.numDigits = 'numDigitsInvalid';
  }
  if (
    task.dtmfInputTimeout !== undefined &&
    (typeof task.dtmfInputTimeout !== 'number' ||
      !Number.isFinite(task.dtmfInputTimeout) ||
      task.dtmfInputTimeout < 1 ||
      task.dtmfInputTimeout > 60)
  ) {
    errors.dtmfInputTimeout = 'inputTimeoutInvalid';
  }
  if (task.dtmfStopEvent !== undefined && !DTMF_STOP_EVENTS.has(task.dtmfStopEvent)) {
    errors.dtmfStopEvent = 'stopEventInvalid';
  }
  if (task.askForConfirmation !== undefined && typeof task.askForConfirmation !== 'boolean') {
    errors.askForConfirmation = 'confirmationInvalid';
  }
};

export const validateTaskFlow = (group: TaskGroupEditorValue | null): TaskFlowErrors => {
  if (!group) return EMPTY_TASK_FLOW_ERRORS;

  const errors: TaskFlowErrors = { groupErrors: {}, taskErrors: {} };
  if (group.tasks.length > 0 && !TASK_FLOW_ID_PATTERN.test(group.id)) {
    errors.groupErrors.id = 'groupIdInvalid';
  }
  const seenIds = new Set<string>();

  // Empty local groups are intentional: their canonical save payload is null.
  group.tasks.forEach((task) => {
    const taskErrors: Record<string, string> = {};

    if (!task.id) {
      taskErrors.id = 'idRequired';
    } else if (!TASK_ID_PATTERN.test(task.id)) {
      taskErrors.id = 'idInvalid';
    }
    if (task.id && seenIds.has(task.id)) {
      taskErrors.id = 'idDuplicate';
    }
    if (task.id) {
      seenIds.add(task.id);
    }

    if (!task.description?.trim()) {
      taskErrors.description = 'descriptionRequired';
    }

    if (task.type === undefined || !(task.type in TASK_TYPE_REGISTRY)) {
      taskErrors.type = 'typeUnsupported';
    } else if (task.type === 'collect_data') {
      validateCollectDataSchema(task, taskErrors);
    } else if (task.type === 'get_dtmf') {
      validateDtmf(task, taskErrors);
    }

    if (
      task.instructions !== undefined &&
      task.instructions !== null &&
      typeof task.instructions !== 'string'
    ) {
      taskErrors.instructions = 'instructionsInvalid';
    }

    if (Object.keys(taskErrors).length > 0) {
      errors.taskErrors[task.clientKey] = taskErrors;
    }
  });

  return errors;
};

export const hasTaskFlowErrors = (errors: TaskFlowErrors): boolean =>
  Object.keys(errors.groupErrors).length > 0 || Object.keys(errors.taskErrors).length > 0;
